import React, { useState, useEffect, useCallback } from 'react';
import {
  ComposedChart, AreaChart, Area, Bar, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import './SAAnalytics.css';

const BASE = 'https://fotileams.com/api';

// Boss portal uses its own token — NOT the employee `token`.
function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('super_token')}`,
  };
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const CITY_COLOR = {
  Karachi: '#10b981', Lahore: '#3b82f6',
  Islamabad: '#8b5cf6', Multan: '#f97316',
};
const colorFor = (c) => CITY_COLOR[c] || '#94a3b8';

function formatPKR(n) {
  const v = Number(n) || 0;
  if (v >= 1000000) return `PKR ${(v / 1000000).toFixed(2)}M`;
  if (v >= 1000)    return `PKR ${Math.round(v / 1000)}K`;
  return `PKR ${v.toLocaleString('en-PK')}`;
}

function getInitials(name) {
  if (!name) return '?';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[p.length - 1]?.[0] || '')).toUpperCase();
}

// Delta chip — green up, red down, grey flat.
function Delta({ value, suffix = '', invert = false }) {
  if (value === null || value === undefined) return null;
  const flat = Math.abs(value) < 0.05;
  const good = invert ? value < 0 : value > 0;
  const cls  = flat ? 'flat' : good ? 'up' : 'down';
  const arrow = flat ? '→' : value > 0 ? '↑' : '↓';
  return (
    <span className={`an-delta an-delta-${cls}`}>
      {arrow} {Math.abs(value)}{suffix}
    </span>
  );
}

// Tiny inline sparkline — six months of a city's attendance rate.
function Spark({ data, color }) {
  if (!data || data.length < 2) return null;
  const w = 66, h = 22, pad = 2;
  const min = Math.min(...data), max = Math.max(...data);
  const span = (max - min) || 1;
  const pts = data.map((v, i) => {
    const x = pad + (i * (w - pad * 2)) / (data.length - 1);
    const y = h - pad - ((v - min) / span) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const last = data[data.length - 1], first = data[0];
  return (
    <svg width={w} height={h} className="an-spark">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
      <circle
        cx={pad + ((data.length - 1) * (w - pad * 2)) / (data.length - 1)}
        cy={h - pad - ((last - min) / span) * (h - pad * 2)}
        r="2.6" fill={last >= first ? '#10b981' : '#ef4444'} />
    </svg>
  );
}

const ChartTip = ({ active, payload, label, unit = '%' }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="an-tip">
      <div className="an-tip-label">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="an-tip-row">
          <span className="an-tip-dot" style={{ background: p.color || p.fill }} />
          <span className="an-tip-name">{p.name}</span>
          <span className="an-tip-val">
            {p.name === 'Deduction' ? formatPKR(p.value) : `${p.value}${unit}`}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function SAAnalytics() {
  const now = new Date();
  const [month, setMonth]   = useState(now.getMonth() + 1);
  const [year, setYear]     = useState(now.getFullYear());
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState(null);
  const [riskFilter, setRiskFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      setLoad(true); setError(null);
      const res = await fetch(`${BASE}/analytics/executive?month=${month}&year=${year}`,
        { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) { setError(json.error || 'Failed to load analytics'); setData(null); }
      else setData(json);
    } catch {
      setError('Network error — could not reach the server');
      setData(null);
    } finally { setLoad(false); }
  }, [month, year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const years = [];
  for (let y = now.getFullYear(); y >= 2024; y--) years.push(y);

  if (loading) {
    return (
      <div className="an-root">
        <div className="an-loading">
          <div className="an-spinner" />
          <p>Fusing attendance, leave, payroll and shift data…</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="an-root">
        <div className="an-error">
          <div className="an-error-icon">⚠</div>
          <div className="an-error-title">Couldn't load analytics</div>
          <div className="an-error-sub">{error}</div>
          <button className="an-retry" onClick={fetchData}>Retry</button>
        </div>
      </div>
    );
  }

  const { today, company, trend, cities, departments, shifts,
          leave_burn: burn, payroll_trend: payroll,
          standings, standings_total, risks, risks_total, meta } = data;

  const trendData = trend.map(t => ({
    label: t.label,
    Attendance: t.attendance_rate,
    Punctuality: t.punctuality_rate,
    Hours: t.hours_completion,
  }));

  const payrollData = payroll.map(p => ({
    label: p.label,
    Deduction: p.deduction_pkr,
    Leakage: p.leakage_pct,
  }));

  const worstShift = shifts.length > 1 ? shifts[shifts.length - 1] : null;
  const bestShift  = shifts.length > 1 ? shifts[0] : null;
  const shiftGap   = (bestShift && worstShift)
    ? Math.round((bestShift.punctuality_rate - worstShift.punctuality_rate) * 10) / 10
    : 0;

  const visibleRisks = riskFilter === 'all'
    ? risks : risks.filter(r => r.severity === riskFilter);

  return (
    <div className="an-root">

      {/* ── HEADER ── */}
      <div className="an-header">
        <div>
          <h1 className="an-title">Executive Analytics</h1>
          <p className="an-sub">
            {MONTHS[month - 1]} {year} · {company.headcount} staff ·
            {' '}scored on hours worked, {meta.min_eligible_days}-day minimum
          </p>
        </div>
        <div className="an-period">
          <select className="an-select" value={month}
            onChange={e => setMonth(parseInt(e.target.value))}>
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select className="an-select" value={year}
            onChange={e => setYear(parseInt(e.target.value))}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button className="an-refresh" onClick={fetchData}>↻</button>
        </div>
      </div>

      {/* ── TODAY STRIP ── */}
      <div className="an-today">
        <span className="an-today-dot" />
        <span className="an-today-lbl">Right now</span>
        <span className="an-today-stat"><b>{today.in_now}</b> clocked in</span>
        <span className="an-today-sep" />
        <span className="an-today-stat"><b>{today.present}</b> of {today.expected} expected</span>
        <span className="an-today-sep" />
        <span className="an-today-stat"><b>{today.late}</b> late</span>
        <span className="an-today-sep" />
        <span className="an-today-stat"><b>{today.on_weekly_off}</b> on weekly off</span>
        <span className="an-today-rate">{today.rate}%</span>
      </div>

      {/* ── KPI BAND ── */}
      <div className="an-kpis">
        <div className="an-kpi">
          <div className="an-kpi-lbl">Attendance</div>
          <div className="an-kpi-num">{company.attendance_rate}<span>%</span></div>
          <div className="an-kpi-foot">
            <Delta value={company.delta_attendance} suffix="pts" />
            <span className="an-kpi-vs">vs {company.prev_label}</span>
          </div>
        </div>
        <div className="an-kpi">
          <div className="an-kpi-lbl">Punctuality</div>
          <div className="an-kpi-num">{company.punctuality_rate}<span>%</span></div>
          <div className="an-kpi-foot">
            <Delta value={company.delta_punctuality} suffix="pts" />
            <span className="an-kpi-vs">not scored, tracked</span>
          </div>
        </div>
        <div className="an-kpi an-kpi-hero">
          <div className="an-kpi-lbl">Hours Completed</div>
          <div className="an-kpi-num">{company.hours_completion}<span>%</span></div>
          <div className="an-kpi-foot">
            <Delta value={company.delta_hours} suffix="pts" />
            <span className="an-kpi-vs">{company.worked_hours.toLocaleString()}h of {company.expected_hours.toLocaleString()}h</span>
          </div>
        </div>
        <div className="an-kpi">
          <div className="an-kpi-lbl">Avg Score</div>
          <div className="an-kpi-num">{company.avg_score}<span>/100</span></div>
          <div className="an-kpi-foot">
            <Delta value={company.delta_score} suffix="pts" />
            <span className="an-kpi-vs">{company.scored} scored</span>
          </div>
        </div>
        <div className="an-kpi an-kpi-warn">
          <div className="an-kpi-lbl">Payroll Leakage</div>
          <div className="an-kpi-num an-kpi-num-sm">
            {formatPKR(payroll[payroll.length - 1]?.deduction_pkr)}
          </div>
          <div className="an-kpi-foot">
            <span className="an-kpi-vs">
              {payroll[payroll.length - 1]?.leakage_pct}% of payroll ·
              {' '}{payroll[payroll.length - 1]?.deduction_days} days lost
            </span>
          </div>
        </div>
      </div>

      {/* ── 6-MONTH TREND ── */}
      <div className="an-card">
        <div className="an-card-head">
          <div>
            <div className="an-card-title">Six-Month Trajectory</div>
            <div className="an-card-sub">
              Attendance and hours as bars, punctuality as the line — a gap
              opening between them means people are showing up but not completing days
            </div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={250}>
          <ComposedChart data={trendData} margin={{ top: 10, right: 8, left: -18, bottom: 0 }}>
            <defs>
              <linearGradient id="anAtt" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#818cf8" stopOpacity={1} />
                <stop offset="100%" stopColor="#6366f1" stopOpacity={0.7} />
              </linearGradient>
              <linearGradient id="anHrs" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#34d399" stopOpacity={1} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0.7} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }}
              axisLine={false} tickLine={false} />
            <YAxis domain={[60, 100]} tick={{ fontSize: 11, fill: '#94a3b8' }}
              axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} />
            <Tooltip content={<ChartTip />} cursor={{ fill: 'rgba(99,102,241,0.05)' }} />
            <Bar dataKey="Attendance" fill="url(#anAtt)" radius={[5, 5, 0, 0]} maxBarSize={26} />
            <Bar dataKey="Hours" fill="url(#anHrs)" radius={[5, 5, 0, 0]} maxBarSize={26} />
            <Line type="monotone" dataKey="Punctuality" stroke="#f59e0b" strokeWidth={2.5}
              dot={{ r: 3, fill: '#f59e0b', strokeWidth: 0 }} activeDot={{ r: 5 }} />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="an-legend">
          <span><i style={{ background: '#6366f1' }} />Attendance</span>
          <span><i style={{ background: '#10b981' }} />Hours completed</span>
          <span><i className="an-legend-line" />Punctuality</span>
        </div>
      </div>

      <div className="an-two-col">

        {/* ── CITY LEAGUE ── */}
        <div className="an-card">
          <div className="an-card-head">
            <div>
              <div className="an-card-title">City League</div>
              <div className="an-card-sub">Ranked on hours completed · sparkline is six months of attendance</div>
            </div>
          </div>
          <div className="an-league">
            {cities.map((c, i) => (
              <div key={c.city} className="an-league-row">
                <div className="an-league-rank">{i + 1}</div>
                <div className="an-league-bar-wrap">
                  <div className="an-league-top">
                    <span className="an-league-name">
                      <span className="an-dot" style={{ background: colorFor(c.city) }} />
                      {c.city}
                      <span className="an-league-meta">{c.headcount} staff · off {c.off_day}</span>
                    </span>
                    <span className="an-league-nums">
                      <Spark data={c.sparkline} color={colorFor(c.city)} />
                      <b>{c.hours_completion}%</b>
                      <Delta value={c.delta_attendance} suffix="" />
                    </span>
                  </div>
                  <div className="an-league-track">
                    <div className="an-league-fill"
                      style={{ width: `${c.hours_completion}%`, background: colorFor(c.city) }} />
                  </div>
                  <div className="an-league-sub">
                    attendance {c.attendance_rate}% · punctuality {c.punctuality_rate}% ·
                    {' '}{c.absent_days} absences · {c.overtime_hours}h overtime
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── SHIFT ANALYSIS ── */}
        <div className="an-card">
          <div className="an-card-head">
            <div>
              <div className="an-card-title">Punctuality by Shift</div>
              <div className="an-card-sub">
                Your shifts start at different times and run different lengths.
                Nothing else in the system compares them.
              </div>
            </div>
          </div>

          {shiftGap >= 5 && worstShift && (
            <div className="an-insight">
              <span className="an-insight-ico">💡</span>
              <div>
                <b>{worstShift.shift_name}</b> is <b>{shiftGap} points</b> worse on
                punctuality than {bestShift.shift_name}. A gap this size across
                {' '}{worstShift.headcount} people usually points at the start time or
                the commute, not the individuals.
              </div>
            </div>
          )}

          <div className="an-shifts">
            {shifts.map(s => {
              const rate = s.punctuality_rate;
              const col  = rate >= 90 ? '#10b981' : rate >= 80 ? '#f59e0b' : '#ef4444';
              return (
                <div key={s.shift_name} className="an-shift-row">
                  <div className="an-shift-info">
                    <div className="an-shift-name">{s.shift_name}</div>
                    <div className="an-shift-meta">
                      {s.daily_hours ? `${s.daily_hours}h day` : 'no shift set'} · {s.headcount} staff
                    </div>
                  </div>
                  <div className="an-shift-bar">
                    <div className="an-shift-track">
                      <div className="an-shift-fill" style={{ width: `${rate}%`, background: col }} />
                    </div>
                  </div>
                  <div className="an-shift-pct" style={{ color: col }}>{rate}%</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="an-two-col">

        {/* ── LEAVE BURN ── */}
        <div className="an-card">
          <div className="an-card-head">
            <div>
              <div className="an-card-title">Leave Burn — {year}</div>
              <div className="an-card-sub">Company-wide consumption against total entitlement</div>
            </div>
          </div>
          <div className="an-burn">
            {[
              { k: 'casual', label: 'Casual', color: '#ef4444' },
              { k: 'sick',   label: 'Sick',   color: '#10b981' },
              { k: 'annual', label: 'Annual', color: '#3b82f6' },
            ].map(t => (
              <div key={t.k} className="an-burn-item">
                <div className="an-burn-top">
                  <span className="an-burn-label">{t.label}</span>
                  <span className="an-burn-val">
                    <b>{burn[t.k].used}</b> / {burn[t.k].entitled} days
                  </span>
                </div>
                <div className="an-burn-track">
                  <div className="an-burn-fill"
                    style={{ width: `${Math.min(100, burn[t.k].pct)}%`, background: t.color }} />
                </div>
                <div className="an-burn-pct">{burn[t.k].pct}% consumed</div>
              </div>
            ))}
          </div>

          <div className="an-burn-detail">
            <div className="an-burn-detail-row">
              <span>Absences auto-covered by casual</span>
              <b>{burn.casual_auto_days} days</b>
            </div>
            <div className="an-burn-detail-row">
              <span>Casual eaten by the 3-late policy</span>
              <b>{burn.casual_lost_to_lates} days</b>
            </div>
          </div>

          {burn.employees_out_of_casual > 0 && (
            <div className="an-burn-alert">
              <b>{burn.employees_out_of_casual}</b> employees have no casual leave left —
              their next absence comes straight out of salary.
              {burn.exhaustion.length > 0 && (
                <div className="an-burn-names">
                  {burn.exhaustion.slice(0, 6).map(e => (
                    <span key={e.employee_id} className="an-burn-chip">
                      {e.full_name}
                    </span>
                  ))}
                  {burn.employees_out_of_casual > 6 &&
                    <span className="an-burn-chip an-burn-more">
                      +{burn.employees_out_of_casual - 6} more
                    </span>}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── PAYROLL LEAKAGE ── */}
        <div className="an-card">
          <div className="an-card-head">
            <div>
              <div className="an-card-title">Payroll Leakage</div>
              <div className="an-card-sub">
                PKR lost to unauthorised absence and the late policy, month by month
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={payrollData} margin={{ top: 10, right: 8, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="anLeak" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ef4444" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#ef4444" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }}
                axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                tickFormatter={v => v >= 1000 ? `${Math.round(v / 1000)}K` : v} />
              <Tooltip content={<ChartTip unit="" />} />
              <Area type="monotone" dataKey="Deduction" stroke="#ef4444" strokeWidth={2.5}
                fill="url(#anLeak)" dot={{ r: 3, fill: '#ef4444', strokeWidth: 0 }} />
            </AreaChart>
          </ResponsiveContainer>
          <div className="an-leak-table">
            {payroll.map(p => (
              <div key={p.key} className="an-leak-row">
                <span className="an-leak-month">{p.label}</span>
                <span className="an-leak-days">{p.deduction_days} days</span>
                <span className="an-leak-pkr">{formatPKR(p.deduction_pkr)}</span>
                <span className={`an-leak-pct ${p.leakage_pct >= 4 ? 'hot' : ''}`}>
                  {p.leakage_pct}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── DEPARTMENTS ── */}
      <div className="an-card">
        <div className="an-card-head">
          <div>
            <div className="an-card-title">Department Performance</div>
            <div className="an-card-sub">Hours completed, with the change since last month</div>
          </div>
        </div>
        <div className="an-dept-grid">
          {departments.map(d => {
            const col = d.hours_completion >= 95 ? '#10b981'
              : d.hours_completion >= 90 ? '#f59e0b' : '#ef4444';
            return (
              <div key={d.department} className="an-dept">
                <div className="an-dept-name">{d.department}</div>
                <div className="an-dept-num" style={{ color: col }}>
                  {d.hours_completion}<span>%</span>
                </div>
                <div className="an-dept-track">
                  <div className="an-dept-fill"
                    style={{ width: `${d.hours_completion}%`, background: col }} />
                </div>
                <div className="an-dept-foot">
                  <span>{d.headcount} staff</span>
                  <Delta value={d.delta_attendance} suffix="" />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="an-two-col">

        {/* ── STANDINGS ── */}
        <div className="an-card">
          <div className="an-card-head">
            <div>
              <div className="an-card-title">Who's Winning</div>
              <div className="an-card-sub">
                Employee of the Month standings — hours worked, shift-neutral.
                Top {standings.length} of {standings_total}.
              </div>
            </div>
          </div>
          <div className="an-stand">
            {standings.map(p => (
              <div key={p.employee_id} className={`an-stand-row ${p.rank <= 3 ? `an-top${p.rank}` : ''}`}>
                <div className="an-stand-rank">
                  {p.rank === 1 ? '🥇' : p.rank === 2 ? '🥈' : p.rank === 3 ? '🥉' : p.rank}
                </div>
                <div className="an-stand-av" style={{ background: colorFor(p.city) }}>
                  {getInitials(p.full_name)}
                </div>
                <div className="an-stand-info">
                  <div className="an-stand-name">
                    {p.full_name}
                    {!p.is_eligible && <span className="an-stand-inelig">building up</span>}
                  </div>
                  <div className="an-stand-meta">
                    {p.city} · {p.department || '—'} · {p.worked_hours}h of {p.expected_hours}h
                    {p.overtime_hours > 0 && ` · +${p.overtime_hours}h extra`}
                  </div>
                </div>
                <div className="an-stand-score">
                  <b>{p.score}</b>
                  <span>{p.hours_completion}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── RISK REGISTER ── */}
        <div className="an-card">
          <div className="an-card-head">
            <div>
              <div className="an-card-title">Risk Register</div>
              <div className="an-card-sub">
                Lateness, absence, hours shortfall and leave exhaustion in one place —
                {' '}{risks_total} flagged
              </div>
            </div>
            <div className="an-risk-filters">
              {['all', 'high', 'medium', 'low'].map(f => (
                <button key={f}
                  className={`an-risk-fbtn ${riskFilter === f ? 'on' : ''}`}
                  onClick={() => setRiskFilter(f)}>
                  {f === 'all' ? 'All' : f}
                </button>
              ))}
            </div>
          </div>
          <div className="an-risks">
            {visibleRisks.length === 0 ? (
              <div className="an-risk-empty">✓ Nothing at this severity</div>
            ) : visibleRisks.map(r => (
              <div key={r.employee_id} className={`an-risk an-risk-${r.severity}`}>
                <div className="an-risk-top">
                  <span className="an-risk-name">{r.full_name}</span>
                  <span className={`an-risk-sev an-sev-${r.severity}`}>{r.severity}</span>
                </div>
                <div className="an-risk-meta">{r.city} · {r.department || '—'} · score {r.score}</div>
                <div className="an-risk-flags">
                  {r.flags.map((f, i) => (
                    <span key={i} className={`an-flag an-flag-${f.k}`}>{f.t}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="an-foot">
        Generated {new Date(data.generated_at).toLocaleString('en-PK', { timeZone: 'Asia/Karachi' })} ·
        {' '}Read-only — this screen never modifies any record
      </div>
    </div>
  );
}